import React, { useEffect, useMemo, useState } from 'react'

import { useToggle } from '@app/hooks'
import { isColumnGroup } from '@app/typeGuards'
import { cloneDeep, debounced, isNullable } from '@app/utils'
import { isEmptyObject, objectOmitProperties } from '@app/utils/object'

import { ColumnSearchToolTitle } from './components'
import { initializeColumnsSearch, initializeControlComponent } from './utils'

import type { Column } from '@app/components/base/Table/types'
import type { TableSearch, UseTableSearchReturn, UseTableSearchParams, UpdateTableSearchMapFn } from './types'

export const useTableSearchTool = <ColumnsType = unknown,>({
  debounce = 500,
  initialTableSearch = {},
  columns,
  columnsSearchSchema,
  onTableSearchChange
}: UseTableSearchParams<ColumnsType>): UseTableSearchReturn<ColumnsType> => {
  const searchToolToggle = useToggle(!isEmptyObject(initialTableSearch))

  const [columnsWithSearchTool, setColumnsWithSearchTool] = useState<Array<Column<ColumnsType>>>(columns)
  const [tableSearch, setTableSearchMap] = useState<TableSearch<ColumnsType>>(initialTableSearch)

  const columnsSearch = useMemo(
    () => initializeColumnsSearch({ columns, columnsSearchSchema }),
    [columns, columnsSearchSchema]
  )

  const updateTableSearchFactory = (columnKey: keyof ColumnsType): UpdateTableSearchMapFn => {
    return debounced(tableSearchOption => {
      setTableSearchMap(prevTableSearch => {
        const newTableSearch: TableSearch<ColumnsType> = isNullable(tableSearchOption)
          ? (objectOmitProperties(prevTableSearch, [columnKey]) as TableSearch<ColumnsType>)
          : {
              ...prevTableSearch,
              [columnKey]: tableSearchOption
            }

        onTableSearchChange?.(newTableSearch)

        return newTableSearch
      })
    }, debounce)
  }

  const getColumnsWithSearchTool = (
    isSearchToolOpen: boolean,
    rawColumns: Array<Column<ColumnsType>>
  ): Array<Column<ColumnsType>> => {
    const columns = cloneDeep(rawColumns)

    for (const column of columns) {
      const columnKey = column.key as keyof ColumnsType
      const columnSearchOption = columnsSearch[columnKey]

      if (!columnSearchOption) continue

      if (isSearchToolOpen) {
        const controlComponent = initializeControlComponent({
          tableSearchOption: tableSearch[columnKey],
          columnSearchOption,
          updateTableSearchMap: updateTableSearchFactory(columnKey)
        })

        const { title, titleWrapperProps } = columnSearchOption

        column.title = controlComponent
          ? (
            <ColumnSearchToolTitle
              { ...titleWrapperProps }
              title={ title }
            >
              { controlComponent }
            </ColumnSearchToolTitle>
            )
          : (
              title
            )
      } else {
        column.title = columnSearchOption.title
      }

      if (isColumnGroup(column)) {
        column.children = getColumnsWithSearchTool(isSearchToolOpen, column.children)
      }
    }

    return columns
  }

  const searchToolToggleDispatcher = (prevToggleState: boolean): boolean => {
    const newToggleState = !prevToggleState

    setColumnsWithSearchTool(getColumnsWithSearchTool(newToggleState, columns))

    if (!newToggleState) {
      setTableSearchMap({})
      onTableSearchChange?.({})
    }

    return newToggleState
  }

  const toggleSearchTool = (): void => {
    searchToolToggle.setValue(searchToolToggleDispatcher)
  }

  useEffect(() => {
    setColumnsWithSearchTool(getColumnsWithSearchTool(searchToolToggle.value, columns))
  }, [columns])

  return {
    columns: columnsWithSearchTool,
    isSearchToolOpened: searchToolToggle.value,
    tableSearch,
    toggleSearchTool
  }
}
